import { useMemo } from 'react';

/**
 * Configuration options for useXYScales hook
 */
export type UseXYScalesConfig = {
  /** Minimum y value in the dataset */
  yMin: number;
  /** Maximum y value in the dataset */
  yMax: number;
};

// Define a simplified Scale type for our use case. This is used by visx, but the types from visx are either not properly exported or too complex for our needs.
export type Scale = {
  nice?: boolean;
  zero?: boolean;
  type: 'linear' | 'time' | 'band';
  domain?: [number, number];
  paddingInner?: number;
};
/**
 * Hook to calculate scale configurations for XYChart with consistent padding.
 *
 * Computes scale configs with equal absolute padding on both ends of the y-axis
 * for consistent visual spacing.
 *
 * @example
 * ```tsx
 * const dataMin = Math.min(...data.map(d => Math.min(d.balance.cents, d.invested.cents)));
 * const dataMax = Math.max(...data.map(d => Math.max(d.balance.cents, d.invested.cents)));
 *
 * const { xScale, yScale } = useXYScales({
 *   yMin: dataMin,
 *   yMax: dataMax,
 * });
 *
 * return (
 *   <XYChart xScale={xScale} yScale={yScale} width={500} height={300}>
 *     {children}
 *   </XYChart>
 * );
 * ```
 */
export const useXYScales = ({ yMin: dataMin, yMax: dataMax }: UseXYScalesConfig): { xScale: Scale; yScale: Scale } => {
  const padding = 0.05;

  const xScale = useMemo(
    () => ({
      type: 'time' as const,
    }),
    []
  );

  const yScale = useMemo(() => {
    const range = dataMax - dataMin;
    const paddingAmount = range * padding;

    const yMin = dataMin >= 0 && dataMin - paddingAmount < 0 ? 0 : dataMin - paddingAmount;
    const yMax = dataMax + paddingAmount;

    return {
      type: 'linear' as const,
      domain: [yMin, yMax] as [number, number],
      zero: false,
    };
  }, [dataMin, dataMax, padding]);

  return { xScale, yScale };
};
